import { SupabaseClient } from '@supabase/supabase-js';
import { cache } from 'react';
import {
  ToneCountWithUser,
  Model,
  Training,
  Tone,
  ToneAndChildren,
  UserDetails,
  ToneCount,
  RelatedTone,
} from '@/utils/helpers';
import {
  ARCHITECTURE_VERSION,
  ArchitectureFilter,
  ArchitectureVersion,
  GEAR,
  MODEL_SIZE,
  FORMAT,
} from '../constants';
import { Tables } from '@/types_db';
import { expandLegacyGears } from '../api/normalize-gear';
import { withIsFavorite } from '../api/is-favorite';
import { orderModelsByTonePage } from '@/utils/models/order';

export const getUser = cache(async (supabase: SupabaseClient) => {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  return user;
});

export const getUserDetailsByUsername = cache(
  async (supabase: SupabaseClient, username: string) => {
    const { data: user } = await supabase
      .from('users')
      .select('*')
      .eq('username', username)
      .single();
    return user;
  }
);

export const getUserDetailsAndLatestTonesByUsername = cache(
  async (supabase: SupabaseClient, username: string) => {
    const { data: user, error: userError } = await supabase
      .from('user_public_counts')
      .select(
        `
        id,
        username,
        avatar_url,
        bio,
        display_name,
        is_verified,
        latest_tones:tones_counts!tones_user_id_fkey!inner (
          id, 
          title, 
          created_at
        )
      `
      )
      .eq('username', username)
      .order('created_at', { foreignTable: 'latest_tones', ascending: false })
      .limit(5, { foreignTable: 'latest_tones' })
      .single();

    if (userError) {
      console.error(userError);
      return null;
    }

    const cast = user as unknown as Partial<UserDetails> & { latest_tones: Partial<ToneCount>[] };
    return cast;
  }
);

export const getUserDetails = cache(
  async (supabase: SupabaseClient, opts: { includeEmail?: boolean } = {}) => {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) return null;
    const { data: userDetails } = await supabase
      .from('users')
      .select('*')
      .eq('id', user.id)
      .single();
    return { ...userDetails, ...(opts.includeEmail ? { email: user.email } : {}) };
  }
);

export const getUserDetailsById = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data: userDetails } = await supabase.from('users').select('*').eq('id', userId).single();
  return userDetails;
});

export const getUserApiKeyByUserId = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data: apiKey } = await supabase
    .from('api_keys')
    .select('*')
    .eq('user_id', userId)
    .eq('is_revoked', false)
    .single();
  return apiKey;
});

export const getUserSettings = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data: userSettings } = await supabase
    .from('user_settings')
    .select('*')
    .eq('user_id', userId)
    .single();
  return userSettings;
});

export const getLatestTrainingAndModel = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data } = await supabase
    .from('trainings')
    .select(
      `
      ${defaultTrainingFields},
      models!public_models_training_id_fkey(${defaultModelFields})
    `
    )
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (!data) return null;
  const model = data.models && data.models.length > 0 ? data.models[0] : undefined;
  // remove models from data
  const { models, ...training } = data;
  return { training, model };
});

export const getUserToneById = cache(
  async (supabase: SupabaseClient, userId: string, toneId: number) => {
    const { data: tone } = await supabase
      .from('tones')
      .select('*')
      .eq('user_id', userId)
      .eq('id', toneId)
      .single();
    return tone as Tone;
  }
);

export const defaultModelFields =
  'id, name, model_url, created_at, training_id, size, input_level, output_level, user_id';

export const getModelById = cache(async (supabase: SupabaseClient, id: number) => {
  const { data: model } = await supabase
    .from('models')
    .select(defaultModelFields)
    .eq('id', id)
    .single();
  return model as Partial<Model>;
});

export const getModelsByToneId = cache(async (supabase: SupabaseClient, toneId: number) => {
  const { data: models } = await supabase
    .from('models')
    .select(defaultModelFields)
    .eq('tone_id', toneId);
  return models as Partial<Model>[];
});

export const getTones = cache(async (supabase: SupabaseClient) => {
  const { data: tones } = await supabase
    .from('tones')
    .select('*')
    .order('created_at', { ascending: false });
  return tones as Tone[];
});

export const getTonesByUserId = cache(
  async (
    supabase: SupabaseClient,
    userId: string,
    opts: {
      includePrivate?: boolean;
      page?: number;
      pageSize?: number;
      /**
       * Signed-in viewer for per-user `is_favorite` annotation. Distinct from
       * `userId` (the profile owner) — omit on public profiles where the
       * viewer isn't the owner and the flag isn't needed.
       */
      viewerId?: string | null;
    } = {}
  ) => {
    const { page = 1, pageSize = 10 } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    let query = supabase
      .from('tones_counts')
      .select(
        `
      *,
      user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
    `,
        { count: 'exact' }
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(start, end);

    if (!opts.includePrivate) {
      query = query.eq('is_public', true);
    }

    const { data: _tones, count } = await query;
    const tones = opts.viewerId
      ? await withIsFavorite(supabase, opts.viewerId, (_tones ?? []) as ToneCountWithUser[])
      : ((_tones ?? []) as ToneCountWithUser[]);
    return {
      tones,
      totalCount: count,
      page,
      pageSize,
      totalPages: Math.ceil((count ?? 0) / pageSize),
    };
  }
);

// Newest public tones by a creator, minus the one being viewed. Backs the
// "More Tones From @user" section on the tone page, so the filters match the
// profile listing that the section's View Profile button leads to.
export const getMoreTonesByUserId = cache(
  async (
    supabase: SupabaseClient,
    userId: string,
    { excludeToneId, limit = 4 }: { excludeToneId?: number; limit?: number } = {}
  ): Promise<ToneCountWithUser[]> => {
    let query = supabase
      .from('tones_counts')
      .select(
        `
      *,
      user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
    `
      )
      .eq('user_id', userId)
      .eq('is_public', true);

    if (excludeToneId) {
      query = query.neq('id', excludeToneId);
    }

    const { data: tones } = await query
      // Public-but-never-published rows carry a null published_at; created_at
      // keeps them ordered instead of floating to the top.
      .order('published_at', { ascending: false, nullsFirst: false })
      .order('created_at', { ascending: false })
      .limit(limit);

    return (tones ?? []) as ToneCountWithUser[];
  }
);

// The query vector for a tone's related-tones search: its featured model, or
// the nearest thing to it that has an embedding. `match_similar_models` takes a
// model rather than a tone, so a tone-level section has to nominate one — and
// the featured model is the one the page already puts forward (it is what the
// slim player autoselects, see getFeaturedModelAndTagsByToneId), so
// recommendations track what the visitor is actually hearing.
//
// Same filters and ordering as that query, minus its extension/A2 legs: those
// exist so the preview is playable in the browser, which has nothing to do with
// similarity, and A2 would drop embedded A1 models for no reason.
// Requires a service_role client: `model_embeddings` has RLS on with no
// policies (20260820120000_model_embeddings_table.sql).
export const getFeaturedEmbeddedModelIdForTone = cache(
  async (supabase: SupabaseClient, toneId: number): Promise<number | null> => {
    const { data, error } = await orderModelsByTonePage(
      supabase
        .from('models')
        .select('id, tones!inner (id), model_embeddings!inner (model_id)')
        .eq('tone_id', toneId)
        .eq('is_deleted', false)
        .not('model_url', 'is', null)
        .not('model_embeddings.embedding', 'is', null)
        // The answer is shared across viewers by a CDN cache, so it has to be
        // viewer-independent: a private or deleted tone gets no related tones,
        // not even for its owner.
        .eq('tones.is_public', true)
        .eq('tones.is_deleted', false)
    )
      // Ordered and capped in Postgres rather than sorted here: an unordered
      // select is truncated arbitrarily at PostgREST's max_rows.
      .limit(1)
      .maybeSingle();

    if (error) throw error;

    return data?.id ?? null;
  }
);

// tones_aggs.trending_score = (downloads + 2*favorites) / age_in_days.
// Floor for related-tones neighbours: same 0.5 bar A2 retrain uses as
// "actually trending-ish". A 1-year-old pack needs ~183 downloads (or
// ~91 favorites). Keep in sync with the SQL default on match_similar_models.
export const RELATED_TONES_MIN_TRENDING_SCORE = 0.5;

// Embeddings-backed related tones for a model, hydrated into tone cards.
// The RPC (20260902200000_match_similar_models_min_trending_score.sql)
// already excludes the source tone, anything non-public or deleted, and
// packs below RELATED_TONES_MIN_TRENDING_SCORE, and returns at most one
// model per tone; it is plain `stable` plpgsql over an RLS'd table, so
// this also needs a service_role client.
export const getRelatedTonesByModelId = cache(
  async (
    supabase: SupabaseClient,
    modelId: number,
    { limit = 4 }: { limit?: number } = {}
  ): Promise<RelatedTone[]> => {
    const { data: matches, error: matchError } = await supabase.rpc('match_similar_models', {
      p_model_id: modelId,
      p_match_count: limit,
      p_min_trending_score: RELATED_TONES_MIN_TRENDING_SCORE,
    });

    // Thrown, not swallowed: the caller caches its answer, and an empty array
    // is indistinguishable from a tone that genuinely has no neighbours.
    if (matchError) throw matchError;

    const hits = (matches ?? []) as { tone_id: number; distance: number }[];
    if (!hits.length) return [];

    const { data: tones, error: tonesError } = await supabase
      .from('tones_counts')
      .select(
        `
      *,
      user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
    `
      )
      .in(
        'id',
        hits.map((hit) => hit.tone_id)
      )
      // The RPC filters on these already; repeated here so a stale match can
      // never surface a tone that was hidden, deleted, or fell below the
      // trending floor since.
      .eq('is_public', true)
      .eq('is_deleted', false)
      .gte('trending_score', RELATED_TONES_MIN_TRENDING_SCORE);

    if (tonesError) throw tonesError;

    const byId = new Map(((tones ?? []) as ToneCountWithUser[]).map((tone) => [tone.id, tone]));

    // `in` does not preserve order, so re-impose the RPC's distance ranking.
    return hits.flatMap((hit) => {
      const tone = byId.get(hit.tone_id);
      return tone ? [{ ...tone, distance: hit.distance }] : [];
    });
  }
);

// `esrs` + `has_logs` are needed by the Models UI so polling can
// reactively update ESR pills and the "Logs" button visibility without
// a page refresh. `has_logs` is a Postgres computed column
// (see 20260522123000_trainings_has_logs_fn.sql) — much cheaper than
// pulling the full `logs` body every poll.
export const defaultTrainingFields =
  'id, model_id, status_text, created_at, is_error, is_success, epochs, max_epochs, type, input_audio, output_audio, learning_rate, learning_rate_decay, esr, esrs, error, trigger_id, prediction_id, user_id, best_fit, architecture, has_logs';

// Narrow projection for the TrainingsProvider polling path. Consumers
// of useTrainingLive / useTrainingsLive are Status.tsx, MainStatus.tsx,
// and Models.tsx — together they read the fields below. Skips the
// heavy ones (architecture jsonb, audio URLs, training config, etc.)
// that defaultTrainingFields carries for the CaptureClient form
// prefill via getLatestTrainingAndModel.
//
// Anything that updates over the course of a training MUST be in here
// — otherwise the spread merge in Models.tsx falls back to the SSR
// snapshot and the UI freezes at page-load values until a refresh.
// Specifically:
//   - epochs, esr, esrs: bumped each poll cycle by the orchestrator
//   - has_logs: flips from false → true once the first log batch lands
//   - type: stable but cheap to include for completeness
//   - status_text, is_error, is_success, error, trigger_id: status bar + error modal
export const liveTrainingFields =
  'id, is_error, is_success, status_text, trigger_id, error, epochs, esr, esrs, has_logs, type';

export const getLinkedTones = cache(
  async (
    supabase: SupabaseClient,
    toneId: number,
    {
      includePrivate = false,
    }: { includePrivate?: boolean } = {}
  ): Promise<ToneCountWithUser[]> => {
    // Step 1: groups this tone is a member of.
    const { data: myGroups, error: myGroupsError } = await supabase
      .from('linked_tone_members')
      .select('linked_tone_id')
      .eq('tone_id', toneId);
    if (myGroupsError || !myGroups?.length) return [];
    const groupIds = myGroups.map((g: { linked_tone_id: number }) => g.linked_tone_id);

    // Step 2: all members of those groups except self.
    const { data: members, error: membersError } = await supabase
      .from('linked_tone_members')
      .select('tone_id')
      .in('linked_tone_id', groupIds)
      .neq('tone_id', toneId);
    if (membersError || !members?.length) return [];
    const memberIds = Array.from(
      new Set(members.map((m: { tone_id: number }) => m.tone_id))
    );
    if (memberIds.length === 0) return [];

    // Step 3: tone details for each linked tone, oldest first.
    let query = supabase
      .from('tones_counts')
      .select('*, user:users!tones_user_id_fkey(*)')
      .in('id', memberIds)
      .order('created_at', { ascending: true });
    if (!includePrivate) query = query.eq('is_public', true);
    const { data: linked, error: linkedError } = await query;
    if (linkedError || !linked) return [];
    return linked as ToneCountWithUser[];
  }
);

export const getToneById = cache(
  async (supabase: SupabaseClient, id: number): Promise<Partial<ToneAndChildren> | null> => {
    const { data: tone, error } = await supabase
      .from('tones_counts')
      .select(
        `*,
      user:users!tones_user_id_fkey (*),
      makes:tone_makes (
        make:makes (*)
      ),
      tags:tone_tags (
        tag:tags (*)
      )
    `
      )
      .eq('id', id)
      .single();

    if (error) {
      console.error(error);
      return null;
    }

    if (!tone) return null;

    // Transform the response to flatten the trainings array for each model
    const transformedTone = {
      ...tone,
      makes: tone.makes.map((make: any) => make.make),
      tags: tone.tags.map((tag: any) => tag.tag),
    };

    return transformedTone as Partial<ToneAndChildren>;
  }
);

// Playable file type per platform. Duplicates the extension check in
// utils/player/to-player-tone.ts so the featured-model query can filter
// server-side and return a single row instead of the whole model list.
// Keep the two in sync.
const PLAYABLE_EXTENSION: Partial<Record<FORMAT, string>> = {
  [FORMAT.NAM]: '.nam',
  [FORMAT.IR]: '.wav',
};

/**
 * Fetch a tone's featured model (first playable one in the owner's ordering)
 * + its tags. Ordering and filters mirror `get_tone_with_models` +
 * `to-player-tone.ts` so the mini player autoselects the same model the full
 * players do.
 */
export const getFeaturedModelAndTagsByToneId = cache(async (
  supabase: SupabaseClient,
  id: number,
  platform: FORMAT,
) => {
  let query = supabase
    .from('tones_counts')
    .select(
      `
      id,
      title,
      tags:tone_tags (
        tag:tags (*)
      ),
      models:models!tone_id (id, name, model_url, architecture_version)
    `
    )
    .eq('id', id)
    .eq('models.is_deleted', false)
    .not('models.model_url', 'is', null);

  const extension = PLAYABLE_EXTENSION[platform];
  if (extension) {
    query = query.ilike('models.model_url', `%${extension}`);
  }

  // A2-only preview, NAM tones only — other platforms have no architecture.
  if (platform === FORMAT.NAM) {
    query = query.eq('models.architecture_version', ARCHITECTURE_VERSION.A2);
  }

  const { data: result, error } = await orderModelsByTonePage(query, {
    referencedTable: 'models',
  })
    .limit(1, { referencedTable: 'models' })
    .maybeSingle();

  if (error) {
    console.error('Error fetching featured model with tags:', error);
    return null;
  }

  if (!result || !result.models || result.models.length === 0) {
    return null;
  }

  // Transform the response to flatten the tags array and return with the featured model
  return {
    id: result.id,
    title: result.title,
    tags: result.tags.map((tagRelation: any) => tagRelation.tag),
    model: result.models[0], // Featured model
  };
});

/**
 * Fetch a tone + its models, makes, tags from the `get_tone_with_models` RPC.
 *
 * `architectures` is an optional array of `architecture_version` enum values
 * used to filter NAM models server-side. Non-NAM platforms (IR / aida-x /
 * aa-snapshot / proteus) ignore the filter and always pass through.
 *
 *   - `undefined` / `null` → return all models (RPC default)
 *   - `['1', 'custom']`    → hide A2 NAM models (legacy default during A2 rollout)
 *   - `['2']`              → only A2 NAM models
 */
export const getToneByIdRpc = cache(async (
  supabase: SupabaseClient,
  id: number,
  architectures?: ArchitectureVersion[] | null,
  userId?: string | null,
) => {
  const { data: tone, error } = await supabase.rpc('get_tone_with_models', {
    tone_id: id,
    architectures: architectures ?? undefined,
    // Only honored DB-side for service-role callers; JWT-scoped clients get
    // is_favorite from auth.uid() automatically.
    requesting_user_id: userId ?? undefined,
  });
  if (error) {
    console.error(error);
    return null;
  }
  return tone as ToneAndChildren;
});

export const getToneTokenById = cache(async (supabase: SupabaseClient, id: number) => {
  const { data: tone } = await supabase.from('tones').select('token').eq('id', id).single();
  return tone?.token;
});

export const getToneMetadataById = cache(
  async (supabase: SupabaseClient, id: number): Promise<ToneAndChildren | null> => {
    const { data: tone, error } = await supabase
      .from('tones_counts')
      .select(
        `*,
      user:users!tones_user_id_fkey (*),
      makes:tone_makes (
        make:makes (*)
      ),
      tags:tone_tags (
        tag:tags (*)
      )
    `
      )
      .eq('id', id)
      .single();

    if (error) {
      console.error(error);
      return null;
    }

    if (!tone) return null;

    const transformedTone = {
      ...tone,
      makes: tone.makes.map((make: any) => make.make),
      tags: tone.tags.map((tag: any) => tag.tag),
    };

    return transformedTone as ToneAndChildren;
  }
);

export const getToneByTzId = cache(async (supabase: SupabaseClient, tzId: number) => {
  const { data: tone } = await supabase
    .from('tones')
    .select('id, title')
    .eq('tz_id', tzId)
    .single();

  if (tone) {
    return tone as Partial<Tone>;
  }

  const { data: model, error: modelError } = await supabase
    .from('models')
    .select(
      `id, 
      tone:tones!inner(
        id,
        title
      )
    `
    )
    .eq('tz_id', tzId)
    .single();

  if (modelError) {
    console.error(modelError);
    return null;
  }

  if (model) {
    return model.tone as Partial<Tone>;
  }

  return null;
});

export const getToneByThId = cache(async (supabase: SupabaseClient, thId: string) => {
  const { data: tone, error } = await supabase
    .from('tones')
    .select('id, title')
    .eq('th_id', thId)
    .single();

  if (error) {
    console.error(error);
    return null;
  }

  if (tone) {
    return tone as Partial<Tone>;
  }

  return null;
});

export const getIsFollowing = cache(
  async (supabase: SupabaseClient, followerId: string, followedId: string) => {
    const { data: follows } = await supabase
      .from('follows')
      .select('*')
      .eq('follower_id', followerId)
      .eq('followed_id', followedId)
      .single();
    return !!follows;
  }
);

export const getFollowers = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data: followers } = await supabase
    .from('follows')
    .select(
      `
      follower:user_public_counts!follower_id(
        id, 
        username, 
        avatar_url,
        public_tones_count
      )
    `
    )
    .eq('followed_id', userId);
  return followers ?? [];
});

export const getFollowersPaginated = cache(
  async (
    supabase: SupabaseClient,
    userId: string,
    opts: { page?: number; pageSize?: number } = {}
  ) => {
    const { page = 1, pageSize = 10 } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const { data: followers, count } = await supabase
      .from('follows')
      .select(
        `
      follower:user_public_counts!follower_id(
        id,
        username,
        avatar_url,
        public_tones_count
      )
    `,
        { count: 'exact' }
      )
      .eq('followed_id', userId)
      .range(start, end);

    return {
      followers: (followers ?? []) as { follower: Partial<UserDetails> }[],
      totalCount: count ?? 0,
      page,
      pageSize,
      totalPages: Math.ceil((count ?? 0) / pageSize),
    };
  }
);

export const getFollowing = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data: following } = await supabase
    .from('follows')
    .select(
      `
      followed:user_public_counts!followed_id(
        id, 
        username, 
        avatar_url,
        public_tones_count
      )
    `
    )
    .eq('follower_id', userId);
  return following ?? [];
});

export const getFollowingPaginated = cache(
  async (
    supabase: SupabaseClient,
    userId: string,
    opts: { page?: number; pageSize?: number } = {}
  ) => {
    const { page = 1, pageSize = 10 } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const { data: following, count } = await supabase
      .from('follows')
      .select(
        `
      followed:user_public_counts!followed_id(
        id, 
        username,
        avatar_url,
        public_tones_count
      )
    `,
        { count: 'exact' }
      )
      .eq('follower_id', userId)
      .range(start, end);

    return {
      following: (following ?? []) as { followed: Partial<UserDetails> }[],
      totalCount: count ?? 0,
      page,
      pageSize,
      totalPages: Math.ceil((count ?? 0) / pageSize),
    };
  }
);

export const getUserDetailsAndCountsById = cache(
  async (supabase: SupabaseClient, userId: string) => {
    const { data: user } = await supabase
      .from('user_public_counts')
      .select('*')
      .eq('id', userId)
      .single();
    return user;
  }
);

export const getUserEmailById = cache(async (supabase: SupabaseClient, userId: string) => {
  const { data: user } = await supabase.auth.admin.getUserById(userId);
  return user?.user?.email;
});

export const getFavoriteTonesByUserId = cache(
  async (
    supabase: SupabaseClient,
    userId: string,
    opts: {
      page?: number;
      pageSize?: number;
    } = {}
  ) => {
    const { page = 1, pageSize = 10 } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    let query = supabase
      .from('favorites')
      .select(
        `
      tone:tones_counts!inner(
        *,
        user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
      )
    `,
        { count: 'exact' }
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(start, end);

    const { data: favorites, count } = await query;

    // Rows come from the user's own favorites (RLS restricts reads to self),
    // so is_favorite is true by construction.
    const tones = (favorites?.map((f) => ({ ...f.tone, is_favorite: true })) ??
      []) as unknown as ToneCountWithUser[];
    const totalCount = count ?? 0;
    return {
      tones,
      totalCount,
      page,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
    };
  }
);

export const getDownloadedTonesByUserId = cache(
  async (
    supabase: SupabaseClient,
    userId: string,
    opts: {
      page?: number;
      pageSize?: number;
    } = {}
  ) => {
    const { page = 1, pageSize = 10 } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    // downloads_deduped keeps only the latest download row per (user_id, tone_id),
    // so this is the user's download history with duplicates collapsed.
    const query = supabase
      .from('downloads_deduped')
      .select(
        `
      tone:tones_counts!inner(
        *,
        user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
      )
    `,
        { count: 'exact' }
      )
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(start, end);

    const { data: downloads, count } = await query;

    // Viewer == userId for this helper (own download history under RLS).
    const tones = await withIsFavorite(
      supabase,
      userId,
      (downloads?.map((d) => d.tone) ?? []) as unknown as ToneCountWithUser[]
    );
    const totalCount = count ?? 0;
    return {
      tones,
      totalCount,
      page,
      pageSize,
      totalPages: Math.ceil(totalCount / pageSize),
    };
  }
);

export const getLatestTonesStream = cache(async (supabase: SupabaseClient, platform?: FORMAT) => {
  let query = supabase
    .from('tones_counts')
    .select(
      `
        *,
        user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
      `
    )
    .eq('is_public', true)
    .eq('is_deleted', false)
    .eq('has_model_with_url', true)
    .not('images', 'is', null)
    .in('platform', platform ? [platform] : [FORMAT.NAM, FORMAT.IR])
    .not('description', 'is', null)
    .not('description', 'eq', '')
    // Surface only A2-NAM tones (or non-NAM tones — architecture doesn't apply).
    .or('platform.neq.nam,a2_models_count.gt.0');

  const { data: tones } = await query.order('published_at', { ascending: false }).limit(8);
  return (tones ?? []) as ToneCountWithUser[];
});

export const getTrendingTonesSteams = cache(
  async (supabase: SupabaseClient, platform?: FORMAT) => {
    let query = supabase
      .from('tones_counts')
      .select(
        `
        *,
        user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
      `
      )
      .eq('is_public', true)
      .eq('is_deleted', false)
      .eq('has_model_with_url', true)
      .not('images', 'is', null)
      .not('description', 'is', null)
      .not('description', 'eq', '');

    if (platform) {
      query = query.eq('platform', platform);
    }

    const { data: tones } = await query.order('trending_score', { ascending: false }).limit(6);
    return (tones ?? []) as ToneCountWithUser[];
  }
);

export const getTrendingTonesWithFilter = cache(async (supabase: SupabaseClient, gear?: GEAR) => {
  let query = supabase
    .from('tones_counts')
    .select(
      `
        *,
        user:users!tones_user_id_fkey (
          username,
          avatar_url,
          display_name,
          is_verified
        )
      `
    )
    .eq('is_public', true)
    .eq('is_deleted', false)
    .eq('has_model_with_url', true)
    .not('images', 'is', null)
    .not('description', 'is', null)
    .not('description', 'eq', '')
    // Surface only A2-NAM tones (or non-NAM tones, e.g. the IR gear tab).
    .or('platform.neq.nam,a2_models_count.gt.0');

  if (gear) {
    // Expand full-rig/amp-cab symmetrically so the amp-cab lane returns
    // legacy full-rig rows during (and after) the backfill — same contract
    // as searchTones.
    query = query.in('gear', expandLegacyGears([gear]) ?? [gear]);
  }

  const { data: tones } = await query.order('trending_score', { ascending: false }).limit(6);
  return (tones ?? []) as ToneCountWithUser[];
});

export const getTagsAndMakesByIds = cache(
  async (supabase: SupabaseClient, ids: { tagIds: number[]; makeIds: number[] }) => {
    const { data: tags } = await supabase.from('tags').select('*').in('id', ids.tagIds);
    const { data: makes } = await supabase.from('makes').select('*').in('id', ids.makeIds);
    return { tags, makes };
  }
);

interface SearchTonesParams {
  query: string;
  page: number;
  pageSize: number;
  order: string;
  tags: string[] | null;
  makes: string[] | null;
  gears: string[] | null;
  calibrated: boolean | null;
  sizes: MODEL_SIZE[] | null;
  creators: string[] | null;
  platform?: FORMAT;
  architecture?: ArchitectureFilter;
  verifiedOnly?: boolean;
  /**
   * Viewer id for per-user `is_favorite`. Only needed for service-role
   * clients (the RPC ignores it otherwise and uses auth.uid()).
   */
  userId?: string | null;
}

interface SearchTonesResult {
  tones: ToneCountWithUser[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  cacheTs?: number;
}

function generateSearchCacheKey(params: SearchTonesParams): string {
  return `search-tones-${params.query || ''}-${params.page}-${params.pageSize}-${params.order || ''}-${params.tags?.join('-') || ''}-${params.makes?.join('-') || ''}-${params.gears?.join('_') || ''}-${params.calibrated ? 'true' : 'false'}-${params.sizes?.join('-') || ''}-${params.creators?.join('-') || ''}-${params.platform ? params.platform : ''}-${params.architecture ? params.architecture : ''}-${params.verifiedOnly ? 'true' : 'false'}-${params.userId || ''}`;
}

const searchCache = new Map<string, SearchTonesResult>();

export const searchTones = cache(
  async (
    supabase: SupabaseClient,
    params: SearchTonesParams,
    rpcName?: string
  ): Promise<SearchTonesResult> => {
    const cacheKey = generateSearchCacheKey(params);
    if (searchCache.size > 10) {
      const firstKey = searchCache.keys().next().value;
      if (firstKey) {
        searchCache.delete(firstKey);
      }
    }
    const cachedData = searchCache.get(cacheKey);
    if (typeof window !== 'undefined' && cachedData) {
      if (Date.now() - (cachedData.cacheTs ?? 0) < 1000 * 60 * 5) {
        return cachedData;
      } else {
        searchCache.delete(cacheKey);
      }
    }

    const now = new Date();
    const { data, error } = await supabase.rpc(rpcName || 'search_tones_a2', {
      query_term: params.query,
      page_number: params.page,
      page_size: params.pageSize,
      order_by: params.order,
      tag_names: params.tags,
      make_names: params.makes,
      gear_filters: expandLegacyGears(params.gears),
      is_calibrated: params.calibrated,
      size_filters: params.sizes,
      usernames: params.creators,
      platform_filter: params.platform,
      architecture_filter: params.architecture,
      verified_only: params.verifiedOnly ?? false,
      // Dropped from the JSON body when undefined, so legacy ?rpc= overrides
      // without the param keep working for callers that don't pass a user.
      requesting_user_id: params.userId ?? undefined,
    });
    console.log('search tones', now, Date.now() - now.getTime());
    if (error) {
      console.error(error);
    }
    const tones =
      data?.map((tone: any) => ({
        ...tone,
        user: {
          username: tone.username,
          avatar_url: tone.avatar_url,
          id: tone.user_id,
          is_verified: tone.is_verified,
          display_name: tone.display_name,
        },
      })) || [];
    let totalCount = 0;
    if (data && data.length > 0) {
      totalCount = data[0].total_count;
    }
    const result = {
      tones: tones as ToneCountWithUser[],
      totalCount,
      page: params.page,
      pageSize: params.pageSize,
      totalPages: Math.ceil((totalCount ?? 0) / params.pageSize),
    };
    searchCache.set(cacheKey, { ...result, cacheTs: Date.now() });
    return result;
  }
);

interface SearchAggregateParams {
  query: string;
  page: number;
  pageSize: number;
  tags: string[] | null;
  makes: string[] | null;
  gears: string[] | null;
  calibrated: boolean | null;
  sizes: MODEL_SIZE[] | null;
  creators: string[] | null;
  platform?: FORMAT;
  architecture?: ArchitectureFilter;
  verifiedOnly?: boolean;
}

interface SearchTagsAggregateResult {
  tag_counts: { tag_id: number; count: number }[];
  total_count: number;
  cacheTs?: number;
}

function generateSearchTagsAggregateCacheKey(params: SearchAggregateParams): string {
  return `search-tags-aggregate-${params.query || ''}-${params.page}-${params.pageSize}-${params.tags?.join('_') || ''}-${params.makes?.join('_') || ''}-${params.gears?.join('_') || ''}-${params.calibrated ? 'true' : 'false'}-${params.sizes?.join('-') || ''}-${params.creators?.join('-') || ''}-${params.platform ? params.platform : ''}-${params.architecture ? params.architecture : ''}-${params.verifiedOnly ? 'true' : 'false'}`;
}

const searchTagsAggregateCache = new Map<string, SearchTagsAggregateResult>();

export const getSearchTagsAggregate = cache(
  async (supabase: SupabaseClient, params: SearchAggregateParams, rpcName?: string) => {
    const cacheKey = generateSearchTagsAggregateCacheKey(params);
    if (searchTagsAggregateCache.size > 10) {
      const firstKey = searchTagsAggregateCache.keys().next().value;
      if (firstKey) {
        searchTagsAggregateCache.delete(firstKey);
      }
    }
    const cachedData = searchTagsAggregateCache.get(cacheKey);
    if (cachedData) {
      if (Date.now() - (cachedData.cacheTs ?? 0) < 1000 * 60 * 5) {
        return cachedData;
      } else {
        searchTagsAggregateCache.delete(cacheKey);
      }
    }
    const { data, error } = await supabase.rpc(rpcName || 'get_search_tags_aggregate_a2', {
      query_term: params.query,
      page_number: params.page,
      page_size: params.pageSize,
      tag_names: params.tags,
      make_names: params.makes,
      gear_filters: expandLegacyGears(params.gears),
      is_calibrated: params.calibrated,
      size_filters: params.sizes,
      usernames: params.creators,
      platform_filter: params.platform,
      architecture_filter: params.architecture,
      verified_only: params.verifiedOnly ?? false
    });
    if (error) {
      console.error(error);
    }
    const result = data.length > 0 ? data[0] : { tag_counts: [], total_count: 0 };
    searchTagsAggregateCache.set(cacheKey, { ...result, cacheTs: Date.now() });
    return result;
  }
);

interface SearchMakesAggregateResult {
  make_counts: { make_id: number; count: number }[];
  total_count: number;
  cacheTs?: number;
}

function generateSearchMakesAggregateCacheKey(params: SearchAggregateParams): string {
  return `search-makes-aggregate-${params.query || ''}-${params.page}-${params.pageSize}-${params.tags?.join('_') || ''}-${params.makes?.join('_') || ''}-${params.gears?.join('_') || ''}-${params.calibrated ? 'true' : 'false'}-${params.sizes?.join('-') || ''}-${params.creators?.join('-') || ''}-${params.platform ? params.platform : ''}-${params.architecture ? params.architecture : ''}-${params.verifiedOnly ? 'true' : 'false'}`;
}

const searchMakesAggregateCache = new Map<string, SearchMakesAggregateResult>();

export const getSearchMakesAggregate = cache(
  async (supabase: SupabaseClient, params: SearchAggregateParams, rpcName?: string) => {
    const cacheKey = generateSearchMakesAggregateCacheKey(params);
    if (searchMakesAggregateCache.size > 10) {
      const firstKey = searchMakesAggregateCache.keys().next().value;
      if (firstKey) {
        searchMakesAggregateCache.delete(firstKey);
      }
    }
    const cachedData = searchMakesAggregateCache.get(cacheKey);
    if (cachedData) {
      if (Date.now() - (cachedData.cacheTs ?? 0) < 1000 * 60 * 5) {
        return cachedData;
      } else {
        searchMakesAggregateCache.delete(cacheKey);
      }
    }
    const { data, error } = await supabase.rpc(rpcName || 'get_search_makes_aggregate_a2', {
      query_term: params.query,
      page_number: params.page,
      page_size: params.pageSize,
      tag_names: params.tags,
      make_names: params.makes,
      gear_filters: expandLegacyGears(params.gears),
      is_calibrated: params.calibrated,
      size_filters: params.sizes,
      usernames: params.creators,
      platform_filter: params.platform,
      architecture_filter: params.architecture,
      verified_only: params.verifiedOnly ?? false
    });
    if (error) {
      console.error(error);
    }
    const result = data.length > 0 ? data[0] : { make_counts: [], total_count: 0 };
    searchMakesAggregateCache.set(cacheKey, { ...result, cacheTs: Date.now() });
    return result;
  }
);

interface SearchCreatorsAggregateResult {
  username_counts: { username: string; count: number }[];
  total_count: number;
  cacheTs?: number;
}

function generateSearchCreatorsAggregateCacheKey(params: SearchAggregateParams): string {
  return `search-creators-aggregate-${params.query || ''}-${params.page}-${params.pageSize}-${params.tags?.join('_') || ''}-${params.makes?.join('_') || ''}-${params.gears?.join('_') || ''}-${params.calibrated ? 'true' : 'false'}-${params.sizes?.join('-') || ''}-${params.creators?.join('-') || ''}-${params.platform ? params.platform : ''}-${params.architecture ? params.architecture : ''}-${params.verifiedOnly ? 'true' : 'false'}`;
}

const searchCreatorsAggregateCache = new Map<string, SearchCreatorsAggregateResult>();

export const getSearchCreatorsAggregate = cache(
  async (supabase: SupabaseClient, params: SearchAggregateParams, rpcName?: string) => {
    const cacheKey = generateSearchCreatorsAggregateCacheKey(params);
    if (searchCreatorsAggregateCache.size > 10) {
      const firstKey = searchCreatorsAggregateCache.keys().next().value;
      if (firstKey) {
        searchCreatorsAggregateCache.delete(firstKey);
      }
    }
    const cachedData = searchCreatorsAggregateCache.get(cacheKey);
    if (cachedData) {
      if (Date.now() - (cachedData.cacheTs ?? 0) < 1000 * 60 * 5) {
        return cachedData;
      } else {
        searchCreatorsAggregateCache.delete(cacheKey);
      }
    }
    const { data, error } = await supabase.rpc(rpcName || 'get_search_usernames_aggregate_a2', {
      query_term: params.query,
      page_number: params.page,
      page_size: params.pageSize,
      tag_names: params.tags,
      make_names: params.makes,
      gear_filters: expandLegacyGears(params.gears),
      is_calibrated: params.calibrated,
      size_filters: params.sizes,
      usernames: params.creators,
      platform_filter: params.platform,
      architecture_filter: params.architecture,
      verified_only: params.verifiedOnly ?? false
    });
    if (error) {
      console.error(error);
    }
    const result = data.length > 0 ? data[0] : { username_counts: [], total_count: 0 };
    searchCreatorsAggregateCache.set(cacheKey, { ...result, cacheTs: Date.now() });
    return result;
  }
);

// Comment queries
// Get top-level comments only for SSR
export const getToneComments = cache(
  async (supabase: SupabaseClient, toneId: number, limit = 50, offset = 0, sort = 'popular') => {
    let orderBy;
    let ascending;
    if (sort === 'popular') {
      orderBy = 'net_votes';
      ascending = false;
    } else if (sort === 'newest') {
      orderBy = 'created_at';
      ascending = false;
    } else if (sort === 'oldest') {
      orderBy = 'created_at';
      ascending = true;
    } else {
      orderBy = 'net_votes';
      ascending = false;
    }
    let query = supabase
      .from('comment_threads')
      .select('*')
      .eq('tone_id', toneId)
      .is('parent_id', null) // Only top-level comments
      .order(orderBy, { ascending });

    // Add fallback ordering for popular sort - if net_votes are the same, sort by newest
    if (sort === 'popular') {
      query = query.order('created_at', { ascending: false });
    }

    const { data, error } = await query.range(offset, offset + limit - 1);

    if (error) {
      console.error('Error fetching comments:', error);
      return [];
    }

    return data as Tables<'comment_threads'>[];
  }
);

export const getToneCommentById = cache(async (supabase: SupabaseClient, id: number) => {
  const { data, error } = await supabase.from('comment_threads').select('*').eq('id', id).single();
  if (error) {
    console.error('Error fetching comment:', error);
    return null;
  }
  return data;
});

// Get all replies for a tone that have the specified top-level parent
export const getCommentReplies = async (
  supabase: SupabaseClient,
  toneId: number,
  topLevelCommentId: number,
  limit = 50,
  offset = 0
) => {
  // Get all non-top-level comments for this tone
  const { data, error } = await supabase
    .from('comment_threads')
    .select('*')
    .eq('tone_id', toneId)
    .not('parent_id', 'is', null) // All replies (not top-level)
    .order('created_at', { ascending: true })
    .range(offset, offset + limit - 1);

  if (error) {
    console.error('Error fetching replies:', error);
    return [];
  }

  // Client-side flattening: find all replies that belong to this thread
  const findAllRepliesInThread = (parentId: number, comments: any[]): any[] => {
    const directReplies = comments.filter((c) => c.parent_id === parentId);
    const allReplies = [...directReplies];

    // For each direct reply, find its replies recursively
    directReplies.forEach((reply) => {
      allReplies.push(...findAllRepliesInThread(reply.id, comments));
    });

    return allReplies;
  };

  const threadReplies = findAllRepliesInThread(topLevelCommentId, data);
  threadReplies.sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime());
  return threadReplies;
};

// Search users for mentions
export const searchUsers = async (supabase: SupabaseClient, query: string, limit = 10) => {
  if (!query || query.length < 1) {
    return [];
  }

  const { data, error } = await supabase
    .from('users')
    .select('username, avatar_url')
    .ilike('username', `%${query}%`)
    .not('username', 'is', null)
    .limit(limit);

  if (error) {
    console.error('Error searching users:', error);
    return [];
  }

  return data
    .map((user) => ({
      username: user.username || '',
      avatar_url: user.avatar_url || '',
    }))
    .filter((user) => user.username);
};

export const getNotificationsByUserId = cache(
  async (
    supabase: SupabaseClient,
    userId: number,
    opts: {
      page?: number;
      pageSize?: number;
    } = {}
  ) => {
    const { page = 1, pageSize = 20 } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    const {
      data: notifications,
      count,
      error,
    } = await supabase
      .from('notifications_details')
      .select('*', { count: 'exact' })
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .range(start, end);

    if (error) {
      console.error('Error fetching notifications:', error);
      return {
        notifications: [],
        totalCount: 0,
        page,
        pageSize,
        totalPages: 0,
      };
    }

    return {
      notifications: notifications || [],
      totalCount: count || 0,
      page,
      pageSize,
      totalPages: Math.ceil((count || 0) / pageSize),
    };
  }
);

export const markNotificationAsRead = async (supabase: SupabaseClient, notificationId: number) => {
  const { data, error } = await supabase
    .from('notifications')
    .update({ is_read: true })
    .eq('id', notificationId);
  if (error) {
    console.error('Error marking notification as read:', error);
  }
  return data;
};

export const hasUnreadNotifications = cache(async (supabase: SupabaseClient, userId: number) => {
  const { count, error } = await supabase
    .from('notifications')
    .select('*', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('is_read', false);

  if (error) {
    console.error('Error checking unread notifications:', error);
    return false;
  }

  return (count ?? 0) > 0;
});

// Leaderboard queries
export const getLeaderboardUsers = cache(
  async (
    supabase: SupabaseClient,
    opts: {
      sortBy?: 'tones' | 'downloads' | 'favorites' | 'models';
      page?: number;
      pageSize?: number;
      verifiedOnly?: boolean;
    } = {}
  ) => {
    const { sortBy = 'tones', page = 1, pageSize = 10, verifiedOnly = false } = opts;
    const start = (page - 1) * pageSize;
    const end = start + pageSize - 1;

    let orderColumn;
    switch (sortBy) {
      case 'downloads':
        orderColumn = 'downloads_count';
        break;
      case 'favorites':
        orderColumn = 'favorites_count';
        break;
      case 'models':
        orderColumn = 'public_models_count';
        break;
      case 'tones':
      default:
        orderColumn = 'public_tones_count';
        break;
    }

    let query = supabase
      .from('user_leaderboard')
      .select('*', { count: 'exact' })
      .gt(orderColumn, 0); // Only include users with at least 1 in the selected metric

    if (verifiedOnly) {
      query = query.eq('is_verified', true);
    }

    const { data: users, count } = await query
      .order(orderColumn, { ascending: false })
      .order('username', { ascending: true }) // Secondary sort for ties
      .range(start, end);

    return {
      users: users as Tables<'user_leaderboard'>[],
      totalCount: count,
      page,
      pageSize,
      totalPages: Math.ceil((count ?? 0) / pageSize),
    };
  }
);

/** Every verified creator with at least one public tone, unordered. */
export const getFeaturedCreatorPool = cache(async (supabase: SupabaseClient) => {
  const { data, error } = await supabase
    .from('user_public_counts')
    .select('*')
    .eq('is_verified', true)
    .gt('public_tones_count', 0);

  if (error) {
    console.error('Error fetching featured creator pool:', error);
    return [];
  }

  return data as Tables<'user_public_counts'>[];
});

/**
 * A random sample of the pool. Kept out of the pool fetch itself so a caller
 * that caches the query still re-samples per request.
 */
export const sampleFeaturedCreators = <T,>(pool: T[], count: number): T[] =>
  [...pool].sort(() => Math.random() - 0.5).slice(0, count);

// Leaderboard add query with 6 random verified creators
export const getLeaderboardAddUsers = cache(async (supabase: SupabaseClient) =>
  sampleFeaturedCreators(await getFeaturedCreatorPool(supabase), 6)
);

export const getMostPopularMakes = cache(async (supabase: SupabaseClient, limit: number = 10) => {
  const { data, error } = await supabase
    .from('makes')
    .select('id, name, description, images')
    .not('category_rank', 'is', null)
    .order('category_rank', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching most popular makes:', error);
    return [];
  }

  return data;
});

export const getMakeByName = cache(async (supabase: SupabaseClient, name: string) => {
  const { data, error } = await supabase.from('makes').select('*').eq('name', name).single();

  if (error) {
    console.error('Error fetching make by name:', error);
    return null;
  }

  return data;
});

export const getMostPopularTags = cache(async (supabase: SupabaseClient, limit: number = 10) => {
  const { data, error } = await supabase
    .from('tags')
    .select('id, name, description, images')
    .not('category_rank', 'is', null)
    .order('category_rank', { ascending: true })
    .limit(limit);

  if (error) {
    console.error('Error fetching most popular tags:', error);
    return [];
  }

  return data;
});

export const getTagByName = cache(async (supabase: SupabaseClient, name: string) => {
  const { data, error } = await supabase.from('tags').select('*').eq('name', name).single();

  if (error) {
    console.error('Error fetching tag by name:', error);
    return null;
  }

  return data;
});

export const getCategoryMakesRpc = cache(
  async (supabase: SupabaseClient, excludedIds: number[] = []) => {
    const { data, error } = await supabase.rpc('get_top_makes_categories', {
      excluded_ids: excludedIds,
      limit_count: 50,
    });
    if (error) {
      console.error('Error fetching category makes:', error);
      return null;
    }
    return data;
  }
);

export const getCategoryTagsRpc = cache(
  async (supabase: SupabaseClient, excludedIds: number[] = []) => {
    const { data, error } = await supabase.rpc('get_top_tags_categories', {
      excluded_ids: excludedIds,
      limit_count: 50,
    });
    if (error) {
      console.error('Error fetching category tags:', error);
      return null;
    }
    return data;
  }
);
